import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, Loader2, AlertOctagon, AlertTriangle, Sparkles, CheckCircle2, Settings2 } from "lucide-react";

const POLL_MS = 3000;
const SHOW_FIRST = 40;

// The final check before invoices go to Xero. Errors must be overridden (Accounts
// Manager, with a reason) and warnings marked reviewed before the push is allowed;
// the server enforces the same rule. Remount (change `key`) when the lines change.
export function InvoiceCheckPanel({ lines, reference, dateFrom, dateTo, excluded, onReady, testid = "invoice-check" }) {
  const { can } = useAuth();
  const [check, setCheck] = useState(null);
  const [running, setRunning] = useState(false);
  const [overrideFor, setOverrideFor] = useState(null);   // issue id being overridden
  const [reason, setReason] = useState("");
  const [showAll, setShowAll] = useState(false);

  const included = (it) => !it.participant || !excluded?.has(it.participant);
  const issues = useMemo(() => (check?.issues || []).filter(included), [check, excluded]); // eslint-disable-line react-hooks/exhaustive-deps
  const hidden = (check?.issues || []).length - issues.length;
  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  const openErrors = errors.filter((i) => !i.override).length;
  const openWarnings = warnings.filter((i) => !i.acknowledged).length;
  const aiRunning = check?.ai_status === "running";
  const ready = !!check && !aiRunning && openErrors === 0 && openWarnings === 0;

  useEffect(() => { onReady?.(ready ? check.id : null); }, [ready, check?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!aiRunning) return undefined;
    const t = setTimeout(() => api.invoiceCheck(check.id).then(setCheck).catch(() => {}), POLL_MS);
    return () => clearTimeout(t);
  }, [check, aiRunning]);

  const run = async () => {
    setRunning(true);
    try {
      setCheck(await api.startInvoiceCheck({ reference, date_from: dateFrom, date_to: dateTo, lines }));
      setShowAll(false);
    } catch (e) { toast.error(e.response?.data?.detail || "The invoice check couldn't run"); }
    finally { setRunning(false); }
  };

  const resolve = async (ids, action, why = "") => {
    try {
      setCheck(await api.resolveInvoiceCheck(check.id, { issue_ids: ids, action, reason: why }));
      setOverrideFor(null); setReason("");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update the check"); }
  };

  const ordered = [...errors, ...warnings];
  const shown = showAll ? ordered : ordered.slice(0, SHOW_FIRST);

  return (
    <div className="border-t bg-white px-4 py-3" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-800">Invoice check</span>
        {check && !aiRunning && (ready
          ? <Badge variant="outline" className="text-[11px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`${testid}-ready`}><CheckCircle2 className="h-3 w-3 mr-1" />Ready to send</Badge>
          : <Badge variant="outline" className="text-[11px] bg-rose-50 text-rose-700 border-rose-200" data-testid={`${testid}-blocked`}>
              {openErrors ? `${openErrors} error${openErrors === 1 ? "" : "s"}` : ""}{openErrors && openWarnings ? " · " : ""}{openWarnings ? `${openWarnings} warning${openWarnings === 1 ? "" : "s"} to review` : ""}
            </Badge>)}
        <div className="ml-auto flex items-center gap-2">
          {can.has("settings.ai") && (
            <Link to="/settings/ai/invoice_checks" className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid={`${testid}-ai-settings`}>
              <Settings2 className="h-3 w-3" /> AI settings
            </Link>
          )}
          <Button onClick={run} disabled={running || !lines.length} variant="outline" className="h-8 text-xs" data-testid={`${testid}-run`}>
            {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
            {check ? "Run the check again" : "Run invoice check"}
          </Button>
        </div>
      </div>
      {!check && (
        <p className="text-[11px] text-slate-500 mt-1">
          Checks every line for billing mistakes, and has AI read the shift notes, before anything is sent. Errors must be fixed or overridden by an Accounts Manager; warnings must be reviewed.
        </p>
      )}
      {check && (
        <div className="mt-2 space-y-2">
          <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{check.shift_count} shift(s) · {check.line_count} line(s) checked</span>
            <span>{errors.length} error(s) · {warnings.length} warning(s)</span>
            {hidden > 0 && <span>{hidden} for held-back participants not shown</span>}
            {aiRunning
              ? <span className="text-indigo-600 flex items-center gap-1" data-testid={`${testid}-ai-running`}><Loader2 className="h-3 w-3 animate-spin" /> AI is reading the shift notes…</span>
              : check.ai_status === "done" ? <span className="text-indigo-600 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI review finished</span> : null}
            {openWarnings > 0 && !aiRunning && (
              <button className="text-[#bf3b4f] font-semibold" data-testid={`${testid}-ack-all`}
                onClick={() => resolve(warnings.filter((w) => !w.acknowledged).map((w) => w.id), "acknowledge")}>
                Mark all warnings reviewed
              </button>
            )}
          </div>
          {ordered.length === 0 && !aiRunning && (
            <div className="text-xs text-emerald-700" data-testid={`${testid}-clean`}>No problems found.</div>
          )}
          <ul className="space-y-1.5">
            {shown.map((it) => {
              const isErr = it.severity === "error";
              const done = isErr ? it.override : it.acknowledged;
              return (
                <li key={it.id} className={`rounded border px-3 py-2 text-xs ${done ? "bg-slate-50 border-slate-200 opacity-70" : isErr ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`} data-testid={`${testid}-issue-${it.id}`}>
                  <div className="flex flex-wrap items-start gap-2">
                    {isErr ? <AlertOctagon className="h-3.5 w-3.5 text-rose-600 mt-0.5" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />}
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold text-slate-800">
                        {it.title}
                        {it.source === "ai" && <Badge variant="outline" className="ml-1.5 text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">AI</Badge>}
                      </div>
                      <div className="text-slate-500 text-[11px]">
                        {it.participant_name || ""}{it.participant_name && it.shift ? " · " : ""}
                        {it.shift && <Link to={`/roster?shift=${encodeURIComponent(it.shift)}`} target="_blank" className="underline">{it.shift}</Link>}
                      </div>
                      <div className="text-slate-700 mt-0.5">{it.detail}</div>
                      {done && (
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {isErr ? "Overridden" : "Reviewed"} by {done.by}{done.reason ? `: ${done.reason}` : ""}
                        </div>
                      )}
                      {overrideFor === it.id && (
                        <div className="mt-2 space-y-1.5">
                          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="text-xs"
                            placeholder="Why it's right to send this anyway" data-testid={`${testid}-override-reason`} />
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-[11px] bg-rose-600 hover:bg-rose-700" disabled={!reason.trim()}
                              onClick={() => resolve([it.id], "override", reason.trim())} data-testid={`${testid}-override-confirm`}>Override</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setOverrideFor(null); setReason(""); }}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {done ? (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => resolve([it.id], "reopen")} data-testid={`${testid}-reopen-${it.id}`}>Undo</Button>
                      ) : isErr ? (
                        can.has("invoicing.override_checks") && overrideFor !== it.id && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setOverrideFor(it.id); setReason(""); }} data-testid={`${testid}-override-${it.id}`}>Override</Button>
                        )
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => resolve([it.id], "acknowledge")} data-testid={`${testid}-ack-${it.id}`}>Mark reviewed</Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {!showAll && ordered.length > SHOW_FIRST && (
            <button className="text-[11px] text-[#bf3b4f] font-semibold" onClick={() => setShowAll(true)}>Show all {ordered.length}</button>
          )}
          {openErrors > 0 && !can.has("invoicing.override_checks") && (
            <p className="text-[11px] text-slate-500">Fix the shift and run the check again, or ask an Accounts Manager to override.</p>
          )}
        </div>
      )}
    </div>
  );
}
